import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LanguageIcon from '@mui/icons-material/Language';
import DevicesIcon from '@mui/icons-material/Devices';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LockIcon from '@mui/icons-material/Lock';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyIcon from '@mui/icons-material/Key';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { useTheme } from '../../contexts/ThemeContext';
import {
  normalizePhoneNumber,
  setupPhoneRecaptcha,
  sendPhoneOtpSms,
  mapPhoneAuthError,
} from '../../services/firebase';

import {
  saveStep1Lead,
  updateStep2Lead,
  markStep3PaymentInitiated,
  markStep3TalkToLead,
  markStep3Converted,
  requestUrgentCallback,
  getLeadById,
  submitQuickCallbackLead,
} from '../../services/leadsService';

const WHATSAPP_SUPPORT_NUMBER = '919415967302';

export default function ProgressiveLaunchpadWizard({
  isOpen,
  onClose,
  initialLeadId = null,
  initialStep = 1,
  initialMode = 'callback',
}) {
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';

  const wizBg = isLight ? '#f8fafc' : '#0b0c0e';
  const wizSurface = isLight ? '#ffffff' : '#131418';
  const wizSurfaceElevated = isLight ? '#f1f5f9' : '#0e1015';
  const wizText = isLight ? '#0f172a' : '#f7f7f8';
  const wizSubtext = isLight ? '#475569' : '#94a3b8';
  const wizBody = isLight ? '#334155' : '#cbd5e1';
  const wizBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)';
  const wizBorderInput = isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)';
  const wizCardBg = isLight ? '#ffffff' : 'rgba(255,255,255,0.02)';
  const wizInputBg = isLight ? '#ffffff' : 'rgba(255,255,255,0.04)';
  const wizShadow = isLight ? '0 10px 30px rgba(0,0,0,0.06)' : 'none';
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [leadId, setLeadId] = useState(initialLeadId || '');
  const [savingStep, setSavingStep] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Real Phone Auth & SMS OTP State
  const [phoneInput, setPhoneInput] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [smsCountdown, setSmsCountdown] = useState(0);

  // Other Details Form State (Asked on the SAME page)
  const [startupName, setStartupName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [founderName, setFounderName] = useState('');
  const [projectNote, setProjectNote] = useState('');

  // Callback Confirmed State
  const [callbackConfirmed, setCallbackConfirmed] = useState(false);
  const [callbackSuccessLeadId, setCallbackSuccessLeadId] = useState('');

  // SMS Countdown timer
  useEffect(() => {
    let timer;
    if (smsCountdown > 0) {
      timer = setInterval(() => {
        setSmsCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [smsCountdown]);

  // Step 2 Form State (The Sunk-Cost & Ego Investment)
  const [domainPreference, setDomainPreference] = useState('need_new'); // 'need_new' | 'have_own'
  const [existingDomain, setExistingDomain] = useState('');
  const [industryVibe, setIndustryVibe] = useState('D2C & E-Commerce');
  const [aiGoal, setAiGoal] = useState('Capture WhatsApp Leads');

  // Step 3 State
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Step 4 State
  const [emailAlias1, setEmailAlias1] = useState('founder');
  const [emailAlias2, setEmailAlias2] = useState('support');
  const [assetNotes, setAssetNotes] = useState('');
  const [assetsSaved, setAssetsSaved] = useState(false);

  // Exit-Intent Modal State
  const [showExitIntentModal, setShowExitIntentModal] = useState(false);
  const [exitCallbackRequested, setExitCallbackRequested] = useState(false);
  const exitIntentTriggeredRef = useRef(false);

  // If initialLeadId provided, load existing lead data
  useEffect(() => {
    async function loadExisting() {
      if (initialLeadId) {
        const existing = await getLeadById(initialLeadId);
        if (existing) {
          setLeadId(existing.id || existing.leadId);
          if (existing.startupName) setStartupName(existing.startupName);
          if (existing.whatsapp || existing.phone) setWhatsapp(existing.whatsapp || existing.phone);
          if (existing.email) setEmail(existing.email);
          if (existing.founderName) setFounderName(existing.founderName);
          if (existing.domainPreference) setDomainPreference(existing.domainPreference);
          if (existing.existingDomain) setExistingDomain(existing.existingDomain);
          if (existing.industryVibe) setIndustryVibe(existing.industryVibe);
          if (existing.aiGoal) setAiGoal(existing.aiGoal);

          if (existing.stage === 'STEP_2_SCOPED' || existing.stage === 'STEP_3_PAYMENT_INITIATED') {
            setCurrentStep(3);
          } else if (existing.stage === 'CONVERTED') {
            setCurrentStep(4);
          }
        }
      }
    }
    loadExisting();
  }, [initialLeadId]);

  // Exit Intent Listener: triggers when mouse leaves document towards the top
  useEffect(() => {
    if (!isOpen || currentStep < 2 || currentStep > 3) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 15 && !exitIntentTriggeredRef.current && !showExitIntentModal) {
        exitIntentTriggeredRef.current = true;
        setShowExitIntentModal(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isOpen, currentStep, showExitIntentModal]);

  // Clean domain string for preview
  const cleanDomainSlug = (startupName || 'mybrand')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') || 'mybrand';

  // Handler for closing the modal
  const handleAttemptClose = () => {
    if (currentStep >= 2 && currentStep <= 3 && !exitIntentTriggeredRef.current) {
      exitIntentTriggeredRef.current = true;
      setShowExitIntentModal(true);
      return;
    }
    onClose();
  };

  // 1. Send REAL SMS OTP to user's mobile number
  const handleSendPhoneSms = async (e) => {
    if (e) e.preventDefault();
    setPhoneError('');
    setErrorMsg('');

    if (!phoneInput.trim()) {
      setPhoneError('Please enter your mobile number.');
      return;
    }

    const e164 = normalizePhoneNumber(phoneInput);
    const digitsOnly = e164.replace(/[^0-9]/g, '');
    if (digitsOnly.length < 10) {
      setPhoneError('Please enter a valid 10-digit mobile number with country code (e.g. +91 93733 38696).');
      return;
    }

    setNormalizedPhone(e164);
    setSmsSending(true);

    try {
      const verifier = setupPhoneRecaptcha('wizard-phone-recaptcha-container');
      if (!verifier) {
        throw new Error('Security verification failed. Please refresh the page.');
      }
      const confirmation = await sendPhoneOtpSms(e164, verifier);
      setConfirmationResult(confirmation);
      setSmsCountdown(45);
      setOtpInput('');
    } catch (err) {
      console.error('Phone SMS Send Error:', err);
      setPhoneError(mapPhoneAuthError(err));
    } finally {
      setSmsSending(false);
    }
  };

  // 2. Verify REAL SMS OTP entered by user
  const handleVerifyPhoneSms = async (e) => {
    if (e) e.preventDefault();
    setPhoneError('');
    if (!confirmationResult) {
      setPhoneError('Please click Send OTP first to receive your code via SMS.');
      return;
    }

    const cleanOtp = otpInput.trim();
    if (cleanOtp.length < 6) {
      setPhoneError('Please enter the 6-digit verification code received on your phone.');
      return;
    }

    setVerifyingOtp(true);
    try {
      await confirmationResult.confirm(cleanOtp);
      setIsPhoneVerified(true);
      setVerifiedPhone(normalizedPhone);
      setWhatsapp(normalizedPhone);
      setPhoneError('');
    } catch (err) {
      console.error('Phone Code Verification Error:', err);
      setPhoneError(mapPhoneAuthError(err));
    } finally {
      setVerifyingOtp(false);
    }
  };

  // 3. Reset / Change phone number
  const handleResetPhone = () => {
    setIsPhoneVerified(false);
    setConfirmationResult(null);
    setOtpInput('');
    setPhoneError('');
    setSmsCountdown(0);
  };

  // 4. Submit Callback Request (On SAME page after phone verified)
  const handleRequestInstantCallback = async (e) => {
    if (e) e.preventDefault();
    setPhoneError('');
    setErrorMsg('');

    if (!isPhoneVerified) {
      setPhoneError('Please verify your mobile number with the SMS code above before requesting a callback.');
      return;
    }

    setSavingStep(true);
    try {
      const effectiveName = founderName.trim() || startupName.trim() || 'Verified Lead';
      const effectiveStartup = startupName.trim() || (founderName.trim() ? `${founderName.trim()}'s Venture` : 'Launchpad Lead');

      const result = await submitQuickCallbackLead({
        phone: verifiedPhone,
        email: email.trim(),
        contactMethod: 'phone',
        otpCode: 'SMS_VERIFIED',
        name: effectiveName,
        startupName: effectiveStartup,
        note: projectNote.trim() || `Instant 15-min callback requested for ${effectiveStartup}`,
      });

      if (result.success) {
        setLeadId(result.leadId);
        setCallbackSuccessLeadId(result.leadId);
        setCallbackConfirmed(true);
      }
    } catch (err) {
      console.error('Callback request error:', err);
      setCallbackConfirmed(true);
    } finally {
      setSavingStep(false);
    }
  };

  // 5. STEP 1 SUBMIT -> Save to Firestore & Advance to Step 2 (Blueprint)
  const handleStep1Submit = async (e) => {
    if (e) e.preventDefault();
    setPhoneError('');
    setErrorMsg('');

    if (!isPhoneVerified) {
      setPhoneError('Please verify your mobile number with the SMS code above before proceeding.');
      return;
    }

    if (!startupName.trim()) {
      setErrorMsg('Please enter your startup or brand name.');
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      setErrorMsg('Please enter a valid business/personal email address.');
      return;
    }

    setErrorMsg('');
    setSavingStep(true);

    try {
      const result = await saveStep1Lead({
        startupName,
        whatsapp: verifiedPhone,
        email,
        founderName,
        existingLeadId: leadId || null,
      });

      if (result.success) {
        setLeadId(result.leadId);
        setCurrentStep(2);
      }
    } catch (err) {
      console.error('Step 1 save error:', err);
      setCurrentStep(2);
    } finally {
      setSavingStep(false);
    }
  };

  // STEP 2 SUBMIT -> Update Firestore & Advance to Step 3
  const handleStep2Submit = async () => {
    setSavingStep(true);
    try {
      await updateStep2Lead({
        leadId,
        domainPreference,
        existingDomain,
        industryVibe,
        aiGoal,
      });
      setCurrentStep(3);
    } catch (err) {
      console.error('Step 2 save error:', err);
      setCurrentStep(3);
    } finally {
      setSavingStep(false);
    }
  };

  // STEP 3: "Pay ₹4,999 & Start 48-Hr Delivery Now"
  const handlePayClick = async () => {
    setSavingStep(true);
    await markStep3PaymentInitiated(leadId);
    setSavingStep(false);
    setShowPaymentModal(true);
  };

  // Complete simulated/gateway payment
  const handleConfirmPayment = async () => {
    setIsProcessingPayment(true);
    try {
      await markStep3Converted({
        leadId,
        startupName,
        whatsapp,
        email,
        founderName,
        domainPreference,
        existingDomain,
        industryVibe,
        aiGoal,
        paymentId: `pay_${Date.now()}`,
      });
      setPaymentSuccess(true);
      setShowPaymentModal(false);
      setCurrentStep(4);
    } catch (err) {
      console.error('Conversion recording error:', err);
      setShowPaymentModal(false);
      setCurrentStep(4);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // STEP 3: Secondary Escape Hatch -> Talk to Launch Lead
  const handleTalkToLead = async () => {
    await markStep3TalkToLead(leadId);
    const textMsg = encodeURIComponent(
      `Hi VerveAI! I just configured our ₹4,999 Launchpad blueprint for "${startupName}".\n- Domain: ${domainPreference === 'need_new' ? 'New Domain' : existingDomain || 'Own Domain'}\n- Industry: ${industryVibe}\n- AI Receptionist Goal: ${aiGoal}\n- Contact: ${whatsapp}\n\nI have a quick question before finalizing payment. Can an architect connect?`
    );
    window.open(`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${textMsg}`, '_blank');
  };

  // Exit-Intent: WhatsApp Blueprint Link
  const handleSendBlueprintWhatsApp = () => {
    const resumeLink = `https://verveai.co/resume/${leadId || 'lead_launch'}`;
    const textMsg = encodeURIComponent(
      `Hi VerveAI! Here is my saved ₹4,999 Launchpad reservation for "${startupName || 'my startup'}" with code LAUNCH50.\n\nResume Link: ${resumeLink}\n\nPlease lock in my discount!`
    );
    window.open(`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${textMsg}`, '_blank');
    setShowExitIntentModal(false);
    onClose();
  };

  // Exit-Intent: Request 15-min callback
  const handleRequestCallback = async () => {
    await requestUrgentCallback({
      leadId,
      startupName,
      whatsapp,
    });
    setExitCallbackRequested(true);
    setTimeout(() => {
      setShowExitIntentModal(false);
      onClose();
    }, 2500);
  };

  return (
    <>
      {/* Main Wizard Dialog */}
      <Dialog
        open={isOpen}
        onClose={handleAttemptClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: wizBg,
            color: wizText,
            backgroundImage: isLight
              ? 'radial-gradient(circle at 50% 0%, rgba(6,182,212,0.06) 0%, transparent 60%)'
              : 'radial-gradient(circle at 50% 0%, rgba(6,182,212,0.12) 0%, transparent 60%)',
          },
        }}
      >
        {/* Top Navbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 4 },
            py: 2,
            borderBottom: `1px solid ${wizBorder}`,
            bgcolor: isLight ? 'rgba(255,255,255,0.92)' : 'rgba(11,12,14,0.85)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                bgcolor: 'rgba(6,182,212,0.15)',
                border: '1px solid rgba(6,182,212,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#22d3ee',
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: wizText }}>
                  VerveAI Launchpad
                </Typography>
                <Chip
                  label="LAUNCH50 (50% OFF)"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16,185,129,0.15)',
                    color: '#34d399',
                    border: '1px solid rgba(16,185,129,0.3)',
                    fontWeight: 700,
                    fontSize: '10px',
                    height: 20,
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '11px', color: wizSubtext }}>
                48–72 Hour Turnkey Digital Foundation
              </Typography>
            </Box>
          </Box>

          {/* Step Progress Pills */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            {[
              { num: 1, label: 'Eligibility & Contact' },
              { num: 2, label: 'Scope & Brand' },
              { num: 3, label: 'Lock-In & Delivery' },
              { num: 4, label: 'Asset Drop' },
            ].map((s) => (
              <Box
                key={s.num}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: currentStep >= s.num ? 1 : 0.45,
                  transition: 'opacity 0.2s',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor:
                      currentStep === s.num
                        ? '#06b6d4'
                        : currentStep > s.num
                        ? '#10b981'
                        : isLight
                        ? '#cbd5e1'
                        : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                >
                  {currentStep > s.num ? '✓' : s.num}
                </Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: wizText }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Close button */}
          <IconButton
            onClick={handleAttemptClose}
            sx={{
              color: wizSubtext,
              '&:hover': { color: wizText, bgcolor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress Line */}
        <Box
          sx={{
            width: '100%',
            height: '3px',
            bgcolor: 'rgba(255,255,255,0.05)',
          }}
        >
          <Box
            sx={{
              width: `${(currentStep / 4) * 100}%`,
              height: '100%',
              bgcolor: '#06b6d4',
              transition: 'width 0.4s ease-in-out',
            }}
          />
        </Box>

        {/* Main Wizard Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2.5, sm: 6 },
            maxWidth: '1100px',
            mx: 'auto',
            width: '100%',
          }}
        >
          {/* ========================================================================= */}
          {/* STEP 1: Phone Verification via Real SMS OTP -> Other Details on Same Page */}
          {/* ========================================================================= */}
          {currentStep === 1 && (
            <Box sx={{ maxWidth: '640px', mx: 'auto', pt: { xs: 2, sm: 3 } }}>
              {/* Invisible reCAPTCHA container required for Firebase Phone Auth */}
              <div id="wizard-phone-recaptcha-container"></div>

              {callbackConfirmed ? (
                /* Callback Request Confirmed View */
                <Box
                  sx={{
                    p: { xs: 3, sm: 4.5 },
                    borderRadius: '20px',
                    bgcolor: wizSurface,
                    border: '1.5px solid rgba(16,185,129,0.35)',
                    boxShadow: isLight ? '0 10px 30px rgba(16,185,129,0.08)' : '0 20px 50px rgba(0,0,0,0.5)',
                    textAlign: 'center',
                    animation: 'fadeIn 0.3s ease-in',
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(16,185,129,0.15)',
                      color: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      border: '1px solid rgba(16,185,129,0.3)',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 36 }} />
                  </Box>

                  <Chip
                    label="ALERT DISPATCHED • URGENT PRIORITY"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16,185,129,0.15)',
                      color: '#10b981',
                      fontWeight: 800,
                      fontSize: '10px',
                      letterSpacing: '0.05em',
                      mb: 1.5,
                    }}
                  />

                  <Typography variant="h4" sx={{ fontWeight: 800, color: wizText, fontSize: { xs: '22px', sm: '28px' }, mb: 1 }}>
                    Callback Request Confirmed! ⚡
                  </Typography>

                  <Typography sx={{ color: wizSubtext, fontSize: '14px', maxWidth: '480px', mx: 'auto', mb: 3 }}>
                    Our Lead Launch Architect has received your request. We are reviewing your project and calling your verified phone number within <strong>15 minutes</strong>.
                  </Typography>

                  {/* Verified Details Card */}
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '14px',
                      bgcolor: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${wizBorder}`,
                      mb: 3.5,
                      textAlign: 'left',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: wizSubtext, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Verified Contact Number
                      </Typography>
                      <Chip
                        icon={<VerifiedUserIcon sx={{ fontSize: '12px !important', color: '#10b981 !important' }} />}
                        label="SMS VERIFIED ✓"
                        size="small"
                        sx={{ bgcolor: 'rgba(16,185,129,0.12)', color: '#10b981', fontWeight: 700, fontSize: '10px', height: 22 }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#06b6d4', fontFamily: 'monospace', mb: 1.5 }}>
                      📞 {verifiedPhone}
                    </Typography>
                    {startupName && (
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: wizText, mb: 1 }}>
                        Project: {startupName} {email ? `• ${email}` : ''}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isLight ? '#059669' : '#34d399', fontSize: '12px', fontWeight: 600 }}>
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                      <span>Guaranteed Callback Window: Within 15 Minutes (10 AM - 9 PM IST)</span>
                    </Box>
                    {callbackSuccessLeadId && (
                      <Typography sx={{ fontSize: '11px', color: wizSubtext, mt: 1, fontFamily: 'monospace' }}>
                        Ref Ticket: {callbackSuccessLeadId} • LAUNCH50 (50% OFF) Coupon Locked
                      </Typography>
                    )}
                  </Box>

                  {/* Immediate Action Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                      component="a"
                      href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(`Hi VerveAI! I just verified my number ${verifiedPhone} and requested an urgent 15-minute callback for "${startupName || 'my project'}". Can we connect right away?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        py: 1.5,
                        bgcolor: '#10b981',
                        color: '#fff',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '14px',
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        '&:hover': { bgcolor: '#059669' },
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 18 }} />
                      <span>Need to talk right now? Chat on WhatsApp ↗</span>
                    </Button>

                    <Button
                      onClick={() => {
                        setWhatsapp(verifiedPhone);
                        setCurrentStep(2);
                      }}
                      sx={{
                        py: 1.4,
                        bgcolor: isLight ? '#0f172a' : '#1e293b',
                        color: '#fff',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '13.5px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: isLight ? '#1e293b' : '#334155' },
                      }}
                    >
                      🚀 Optionally Scope Your ₹4,999 Blueprint While Waiting →
                    </Button>

                    <Button
                      onClick={onClose}
                      sx={{
                        color: wizSubtext,
                        fontSize: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { color: wizText },
                      }}
                    >
                      Done • Close Window
                    </Button>
                  </Box>
                </Box>
              ) : (
                /* Main Registration Form on the SAME page */
                <Box
                  sx={{
                    p: { xs: 3, sm: 4.5 },
                    borderRadius: '20px',
                    bgcolor: wizSurface,
                    border: `1px solid ${wizBorder}`,
                    boxShadow: wizShadow,
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: '#06b6d4 !important' }} />}
                      label="STEP 1 • PHONE VERIFICATION & DETAILS"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(6,182,212,0.1)',
                        color: '#22d3ee',
                        border: '1px solid rgba(6,182,212,0.3)',
                        fontWeight: 700,
                        fontSize: '11px',
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '24px', sm: '30px' },
                        color: wizText,
                        mb: 1.2,
                        lineHeight: 1.2,
                      }}
                    >
                      Reserve Your{' '}
                      <span
                        style={{
                          background: 'linear-gradient(90deg, #22d3ee, #818cf8)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        LAUNCH50 (₹4,999)
                      </span>{' '}
                      Coupon
                    </Typography>
                    <Typography sx={{ color: wizSubtext, fontSize: '13.5px' }}>
                      Verify your mobile number via SMS to guarantee your inaugural discount and 48-hr build tracking, then share your startup details below.
                    </Typography>
                  </Box>

                  {phoneError && (
                    <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.12)', color: '#f87171' }}>
                      {phoneError}
                    </Alert>
                  )}

                  {errorMsg && (
                    <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.12)', color: '#f87171' }}>
                      {errorMsg}
                    </Alert>
                  )}

                  {/* ========================================================= */}
                  {/* PART 1: MOBILE NUMBER & REAL SMS VERIFICATION */}
                  {/* ========================================================= */}
                  <Box sx={{ mb: 3.5 }}>
                    {!isPhoneVerified ? (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: wizBody }}>
                            1. Mobile Number (Real SMS Verification) <span style={{ color: '#06b6d4' }}>*</span>
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#10b981', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WhatsAppIcon sx={{ fontSize: 13 }} /> 48-hr build updates
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                          <TextField
                            fullWidth
                            disabled={smsSending || confirmationResult !== null}
                            placeholder="+91 93733 38696"
                            value={phoneInput}
                            onChange={(e) => {
                              setPhoneInput(e.target.value);
                              setPhoneError('');
                            }}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: wizInputBg,
                                color: wizText,
                                borderRadius: '12px',
                                border: `1px solid ${wizBorderInput}`,
                                '&:hover': { border: '1px solid rgba(6,182,212,0.4)' },
                                '&.Mui-focused': { border: '1px solid #06b6d4' },
                              },
                            }}
                          />

                          {!confirmationResult ? (
                            <Button
                              onClick={handleSendPhoneSms}
                              disabled={smsSending || !phoneInput.trim()}
                              sx={{
                                px: 3,
                                py: { xs: 1.5, sm: 'auto' },
                                minWidth: { xs: '100%', sm: '170px' },
                                bgcolor: '#06b6d4',
                                color: '#fff',
                                borderRadius: '12px',
                                fontWeight: 700,
                                fontSize: '13px',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 0 20px rgba(6,182,212,0.3)',
                                '&:hover': { bgcolor: '#0891b2' },
                              }}
                            >
                              {smsSending ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                  <span>Sending SMS...</span>
                                </Box>
                              ) : (
                                'Send OTP via SMS 📱'
                              )}
                            </Button>
                          ) : (
                            <Button
                              onClick={handleResetPhone}
                              sx={{
                                px: 2,
                                color: wizSubtext,
                                border: `1px solid ${wizBorder}`,
                                borderRadius: '12px',
                                fontSize: '12px',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Change Number
                            </Button>
                          )}
                        </Box>

                        {/* Enter SMS Code Box */}
                        {confirmationResult && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2.5,
                              borderRadius: '14px',
                              bgcolor: isLight ? '#ecfeff' : 'rgba(6,182,212,0.08)',
                              border: '1.5px solid rgba(6,182,212,0.35)',
                              animation: 'fadeIn 0.25s ease-in',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                              <KeyIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isLight ? '#0e7490' : '#22d3ee' }}>
                                Enter 6-Digit SMS Verification Code
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '12px', color: wizSubtext, mb: 2 }}>
                              We sent an SMS text message to <strong>{normalizedPhone}</strong>. Enter the 6 digits to verify that you are adding a correct number.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                              <TextField
                                autoFocus
                                placeholder="• • • • • •"
                                value={otpInput}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                                  setOtpInput(val);
                                  setPhoneError('');
                                }}
                                inputProps={{
                                  maxLength: 6,
                                  style: {
                                    textAlign: 'center',
                                    fontSize: '22px',
                                    fontWeight: 800,
                                    letterSpacing: '10px',
                                    fontFamily: 'monospace',
                                  },
                                }}
                                sx={{
                                  maxWidth: { xs: '100%', sm: '260px' },
                                  flex: 1,
                                  '& .MuiInputBase-root': {
                                    bgcolor: wizInputBg,
                                    color: wizText,
                                    borderRadius: '12px',
                                    border: `1.5px solid ${wizBorderInput}`,
                                    '&.Mui-focused': { border: '1px solid #06b6d4' },
                                  },
                                }}
                              />

                              <Button
                                onClick={handleVerifyPhoneSms}
                                disabled={verifyingOtp || otpInput.trim().length !== 6}
                                sx={{
                                  px: 3.5,
                                  py: 1.5,
                                  minWidth: { xs: '100%', sm: '150px' },
                                  bgcolor: '#10b981',
                                  color: '#fff',
                                  fontWeight: 700,
                                  borderRadius: '12px',
                                  textTransform: 'none',
                                  boxShadow: '0 0 20px rgba(16,185,129,0.3)',
                                  '&:hover': { bgcolor: '#059669' },
                                  '&:disabled': { opacity: 0.5, color: '#fff' },
                                }}
                              >
                                {verifyingOtp ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    <span>Verifying...</span>
                                  </Box>
                                ) : (
                                  'Verify Code ✓'
                                )}
                              </Button>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                              {smsCountdown > 0 ? (
                                <Typography sx={{ fontSize: '11px', color: wizSubtext }}>
                                  Resend SMS in {smsCountdown}s
                                </Typography>
                              ) : (
                                <Button onClick={handleSendPhoneSms} sx={{ p: 0, fontSize: '11px', color: '#06b6d4', textTransform: 'none', fontWeight: 700 }}>
                                  Resend Verification SMS
                                </Button>
                              )}
                              <Button onClick={handleResetPhone} sx={{ p: 0, fontSize: '11px', color: wizSubtext, textTransform: 'none' }}>
                                Change number
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      /* Mobile Number Verified Badge */
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '14px',
                          bgcolor: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.12)',
                          border: '1.5px solid #10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: 'rgba(16,185,129,0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#10b981',
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 22 }} />
                          </Box>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: '11px', color: '#10b981', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Mobile Number Verified
                              </Typography>
                              <Chip label="SMS VERIFIED ✓" size="small" sx={{ height: 18, fontSize: '9px', fontWeight: 800, bgcolor: 'rgba(16,185,129,0.2)', color: '#10b981' }} />
                            </Box>
                            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: wizText, fontFamily: 'monospace' }}>
                              📞 {verifiedPhone}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          onClick={handleResetPhone}
                          sx={{ fontSize: '11px', color: wizSubtext, textTransform: 'none', '&:hover': { color: wizText } }}
                        >
                          Change Number
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {/* ========================================================= */}
                  {/* PART 2: OTHER DETAILS ON THE SAME PAGE (UNLOCKED AFTER SMS OTP) */}
                  {/* ========================================================= */}
                  {!isPhoneVerified ? (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        border: `1.5px dashed ${wizBorder}`,
                        bgcolor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
                        textAlign: 'center',
                        mt: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <LockIcon sx={{ fontSize: 18, color: wizSubtext }} />
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: wizText }}>
                          Step 1B: Venture Details & Callback Options (Locked)
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '12.5px', color: wizSubtext, maxWidth: '460px', mx: 'auto', mb: 1.5, lineHeight: 1.5 }}>
                        To verify that you are providing a genuine, reachable mobile number, enter the SMS verification code above. Your startup details, domain check, and guaranteed 15-minute callback request will unlock right here on this page.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '11px', color: wizSubtext }}>
                          SMS taking time?
                        </Typography>
                        <Button
                          component="a"
                          href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent('Hi VerveAI! I want to verify my mobile number and claim the LAUNCH50 ₹4,999 launchpad offer.')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            p: 0,
                            fontSize: '11px',
                            color: '#10b981',
                            textTransform: 'none',
                            fontWeight: 700,
                            minWidth: 0,
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                          }}
                        >
                          Chat directly with Launch Architect on WhatsApp ↗
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    /* Once Phone is Verified: Show Venture Details on the exact SAME page */
                    <Box sx={{ pt: 2.5, borderTop: `1px solid ${wizBorder}`, animation: 'fadeIn 0.3s ease-in' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981' }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: wizText }}>
                          2. Startup & Founder Details (LAUNCH50 50% OFF Reserved ✓)
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                        {/* Startup Name */}
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: wizBody, mb: 0.8 }}>
                            Startup / Brand Name <span style={{ color: '#06b6d4' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth
                            autoFocus
                            placeholder="e.g. Gaon Pure or Orbit AI"
                            value={startupName}
                            onChange={(e) => setStartupName(e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: wizInputBg,
                                color: wizText,
                                borderRadius: '12px',
                                border: `1px solid ${wizBorderInput}`,
                                '&:hover': { border: '1px solid rgba(6,182,212,0.4)' },
                                '&.Mui-focused': { border: '1px solid #06b6d4' },
                              },
                            }}
                          />
                        </Box>

                        {/* Email Address */}
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: wizBody, mb: 0.8 }}>
                            Email Address <span style={{ color: '#06b6d4' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth
                            type="email"
                            placeholder="founder@yourstartup.com or personal email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: wizInputBg,
                                color: wizText,
                                borderRadius: '12px',
                                border: `1px solid ${wizBorderInput}`,
                                '&:hover': { border: '1px solid rgba(6,182,212,0.4)' },
                                '&.Mui-focused': { border: '1px solid #06b6d4' },
                              },
                            }}
                          />
                        </Box>

                        {/* Founder Name */}
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: wizBody, mb: 0.8 }}>
                            Founder / Contact Name <span style={{ color: wizSubtext }}>(Optional)</span>
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder="e.g. Vikram Verma"
                            value={founderName}
                            onChange={(e) => setFounderName(e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: wizInputBg,
                                color: wizText,
                                borderRadius: '12px',
                                border: `1px solid ${wizBorderInput}`,
                                '&:hover': { border: '1px solid rgba(6,182,212,0.4)' },
                                '&.Mui-focused': { border: '1px solid #06b6d4' },
                              },
                            }}
                          />
                        </Box>

                        {/* Specific Note or Question */}
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: wizBody, mb: 0.8 }}>
                            Specific Note or Question for Architect <span style={{ color: wizSubtext }}>(Optional)</span>
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="e.g. Need GST portal integration, launching next week."
                            value={projectNote}
                            onChange={(e) => setProjectNote(e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: wizInputBg,
                                color: wizText,
                                borderRadius: '12px',
                                border: `1px solid ${wizBorderInput}`,
                                '&:hover': { border: '1px solid rgba(6,182,212,0.4)' },
                                '&.Mui-focused': { border: '1px solid #06b6d4' },
                              },
                            }}
                          />
                        </Box>

                        {/* Instant Domain Preview Card (Visual Reward) */}
                        {startupName.trim() && (
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              bgcolor: 'rgba(6,182,212,0.06)',
                              border: '1px solid rgba(6,182,212,0.25)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '8px',
                                  bgcolor: '#06b6d4',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 800,
                                  color: '#fff',
                                  fontSize: '14px',
                                }}
                              >
                                {startupName.charAt(0).toUpperCase()}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: wizText }}>
                                  {startupName}
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#22d3ee', fontFamily: 'monospace' }}>
                                  https://{cleanDomainSlug}.com
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label="COUPON LOCKED (50% OFF)"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(16,185,129,0.2)',
                                color: '#34d399',
                                fontWeight: 800,
                                fontSize: '9px',
                              }}
                            />
                          </Box>
                        )}

                        {/* DUAL ACTION BUTTONS: Instant Callback OR Continue Blueprint */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                          {/* Action 1: Instant 15-Minute Callback */}
                          <Button
                            onClick={handleRequestInstantCallback}
                            disabled={savingStep}
                            sx={{
                              py: 1.6,
                              bgcolor: '#06b6d4',
                              color: '#fff',
                              borderRadius: '12px',
                              fontWeight: 700,
                              fontSize: '14.5px',
                              textTransform: 'none',
                              boxShadow: '0 0 25px rgba(6,182,212,0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              '&:hover': {
                                bgcolor: '#0891b2',
                                boxShadow: '0 0 35px rgba(6,182,212,0.5)',
                              },
                            }}
                          >
                            {savingStep ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                                <span>Alerting Launch Architect...</span>
                              </Box>
                            ) : (
                              <>
                                <PhoneCallbackIcon sx={{ fontSize: 18 }} />
                                <span>Request Guaranteed 15-Minute Callback ⚡</span>
                              </>
                            )}
                          </Button>

                          {/* Action 2: Continue Building Full Blueprint (Step 2) */}
                          <Button
                            onClick={handleStep1Submit}
                            disabled={savingStep}
                            sx={{
                              py: 1.4,
                              bgcolor: isLight ? '#0f172a' : '#1e293b',
                              color: '#fff',
                              borderRadius: '12px',
                              fontWeight: 700,
                              fontSize: '13.5px',
                              textTransform: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              '&:hover': {
                                bgcolor: isLight ? '#1e293b' : '#334155',
                              },
                            }}
                          >
                            <span>Or Continue Full 4-Step Blueprint (Scope Domain & AI) →</span>
                          </Button>
                        </Box>

                        <Typography sx={{ textAlign: 'center', fontSize: '11px', color: wizSubtext, mt: 0.5 }}>
                          🔒 100% Confidential. Instant reservation with guaranteed 50% discount.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* ========================================================================= */}
          {/* STEP 2: The "Sunk-Cost & Ego Investment" (Zero Typing—Just Clicking Pills) */}
          {/* ========================================================================= */}
          {currentStep === 2 && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>
                  Scope Your Day-1 Setup for{' '}
                  <span style={{ color: '#22d3ee' }}>{startupName}</span>
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '14px' }}>
                  Tap 3 quick pills to configure your custom domain, industry theme, and 24/7 AI Receptionist.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' },
                  gap: 4,
                  alignItems: 'start',
                }}
              >
                {/* Left Side: 3 Quick Pill Groups */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                  {/* 1. Domain Preference */}
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mb: 1.5 }}>
                      1. Brand Domain Setup
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {[
                        { id: 'need_new', label: 'I need a new .com / .in domain', icon: <LanguageIcon sx={{ fontSize: 16 }} /> },
                        { id: 'have_own', label: 'I already own a domain / Instagram', icon: <DevicesIcon sx={{ fontSize: 16 }} /> },
                      ].map((item) => (
                        <Box
                          key={item.id}
                          onClick={() => setDomainPreference(item.id)}
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flex: 1,
                            minWidth: '220px',
                            bgcolor: domainPreference === item.id ? (isLight ? '#ecfeff' : 'rgba(6,182,212,0.15)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'),
                            border:
                              domainPreference === item.id
                                ? '1.5px solid #06b6d4'
                                : '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                          }}
                        >
                          <Box sx={{ color: domainPreference === item.id ? '#22d3ee' : '#94a3b8' }}>
                            {item.icon}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: domainPreference === item.id ? 700 : 500,
                              color: domainPreference === item.id ? '#fff' : '#cbd5e1',
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {domainPreference === 'have_own' && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g. zohalibas.com or instagram.com/brand"
                          value={existingDomain}
                          onChange={(e) => setExistingDomain(e.target.value)}
                          sx={{
                            '& .MuiInputBase-root': {
                              bgcolor: 'rgba(255,255,255,0.03)',
                              color: '#fff',
                              borderRadius: '10px',
                              fontSize: '13px',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* 2. Industry / Vibe */}
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mb: 1.5 }}>
                      2. Industry & Brand Vibe
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {[
                        'D2C & E-Commerce',
                        'SaaS & Tech',
                        'Real Estate & Infra',
                        'Healthcare & Services',
                        'Consulting & Agency',
                      ].map((ind) => (
                        <Chip
                          key={ind}
                          label={ind}
                          onClick={() => setIndustryVibe(ind)}
                          sx={{
                            py: 2.2,
                            px: 1,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: industryVibe === ind ? 700 : 500,
                            bgcolor: industryVibe === ind ? '#06b6d4' : 'rgba(255,255,255,0.04)',
                            color: industryVibe === ind ? '#fff' : '#94a3b8',
                            border: industryVibe === ind ? 'none' : '1px solid rgba(255,255,255,0.08)',
                            '&:hover': { bgcolor: industryVibe === ind ? '#0891b2' : 'rgba(255,255,255,0.08)' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* 3. AI Receptionist Goal */}
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mb: 1.5 }}>
                      3. AI Receptionist (Verina) Core Mission
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {[
                        'Capture WhatsApp Leads',
                        'Book Meetings & Demos',
                        'Answer Product FAQs',
                        '24/7 Customer Support',
                      ].map((goal) => (
                        <Chip
                          key={goal}
                          label={goal}
                          onClick={() => setAiGoal(goal)}
                          sx={{
                            py: 2.2,
                            px: 1,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: aiGoal === goal ? 700 : 500,
                            bgcolor: aiGoal === goal ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.04)',
                            color: aiGoal === goal ? '#34d399' : '#94a3b8',
                            border: aiGoal === goal ? '1.5px solid #10b981' : '1px solid rgba(255,255,255,0.08)',
                            '&:hover': { bgcolor: aiGoal === goal ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <Button
                      onClick={() => setCurrentStep(1)}
                      sx={{
                        color: '#94a3b8',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 3,
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} /> Back
                    </Button>
                    <Button
                      fullWidth
                      onClick={handleStep2Submit}
                      disabled={savingStep}
                      sx={{
                        py: 1.6,
                        bgcolor: '#06b6d4',
                        color: '#fff',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '14px',
                        textTransform: 'none',
                        boxShadow: '0 0 20px rgba(6,182,212,0.3)',
                        '&:hover': { bgcolor: '#0891b2' },
                      }}
                    >
                      {savingStep ? 'Saving Blueprint...' : 'Confirm Blueprint & Lock In (₹4,999) →'}
                    </Button>
                  </Box>
                </Box>

                {/* Right Side: Live Dynamic Preview Card (Ego & Ownership) */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(16px)',
                  }}
                >
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#22d3ee', mb: 2, textTransform: 'uppercase' }}>
                    Live Blueprint Preview
                  </Typography>

                  {/* Mock Browser Frame */}
                  <Box
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      bgcolor: '#131418',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Browser top bar */}
                    <Box sx={{ p: 1.5, bgcolor: isLight ? '#e2e8f0' : '#0e0f13', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.8 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          borderRadius: '6px',
                          px: 1.5,
                          py: 0.4,
                          fontSize: '10px',
                          color: '#94a3b8',
                          fontFamily: 'monospace',
                        }}
                      >
                        https://www.{cleanDomainSlug}.com
                      </Box>
                    </Box>

                    {/* Mock Website Canvas */}
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#fff' }}>
                          {startupName}
                        </Typography>
                        <Chip
                          label={industryVibe}
                          size="small"
                          sx={{ fontSize: '9px', bgcolor: 'rgba(255,255,255,0.06)', color: '#cbd5e1' }}
                        />
                      </Box>

                      <Box sx={{ p: 2, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.03)', mb: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#fff', mb: 0.5 }}>
                          Modern Storefront & Web App
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                          5-Page fast Next.js architecture with instant mobile responsiveness.
                        </Typography>
                      </Box>

                      {/* Mock AI Widget */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'rgba(6,182,212,0.1)',
                          border: '1px solid rgba(6,182,212,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <SmartToyIcon sx={{ color: '#22d3ee', fontSize: 20 }} />
                        <Box>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>
                            Verina for {startupName}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: '#34d399' }}>
                            ● Active: {aiGoal}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#10b981', fontSize: '11px' }}>
                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                    <span>Auto-saved to admin console (Lead Score: 85/100)</span>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* ========================================================================= */}
          {/* STEP 3: The Payment & Instant Workspace Lock-In (₹4,999 Checkout) */}
          {/* ========================================================================= */}
          {currentStep === 3 && (
            <Box sx={{ maxWidth: '680px', mx: 'auto', pt: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Chip
                  label="BLUEPRINT SCOPED & LOCKED"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16,185,129,0.15)',
                    color: '#34d399',
                    border: '1px solid rgba(16,185,129,0.3)',
                    fontWeight: 700,
                    fontSize: '11px',
                    mb: 1.5,
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>
                  Activate 48-Hour Delivery for{' '}
                  <span style={{ color: '#22d3ee' }}>{startupName}</span>
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '14px' }}>
                  Complete setup delivered ready-to-launch within 48 to 72 hours.
                </Typography>
              </Box>

              {/* Package Details Box */}
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(6,182,212,0.3)',
                  boxShadow: '0 0 35px rgba(6,182,212,0.15)',
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: `1px solid ${wizBorder}` }}>
                  <Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                      Turnkey Startup Launchpad Package
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                      Everything {startupName} needs to operate online
                    </Typography>
                  </Box>
                  <Chip label="50% OFF" size="small" sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 800, fontSize: '11px' }} />
                </Box>

                {/* 4 Pillars Checklist */}
                <Box sx={{ py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    `Custom Domain (${cleanDomainSlug}.com/.in) + Cloudflare SSL`,
                    'High-Converting 5-Page Modern Web Storefront',
                    'Custom Domain Business Email (@domain with SPF/DKIM)',
                    `24/7 Autonomous AI Receptionist (Verina for ${startupName})`,
                    'Guaranteed Delivery in 48–72 Hours with White-Glove Support',
                  ].map((feat, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#22d3ee' }} />
                      <Typography sx={{ fontSize: '13px', color: wizBody }}>{feat}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Pricing Summary */}
                <Box
                  sx={{
                    pt: 2.5,
                    borderTop: `1px solid ${wizBorder}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                      Inaugural Cross-Site Offer (Code: <strong style={{ color: '#22d3ee' }}>LAUNCH50</strong>)
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#64748b', textDecoration: 'line-through' }}>
                      Regular Price: ₹9,999
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '28px', fontWeight: 900, color: '#fff' }}>
                      ₹4,999
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#34d399', fontWeight: 600 }}>
                      All-Inclusive • One-Time
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Two Strategic Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Primary Button */}
                <Button
                  onClick={handlePayClick}
                  disabled={savingStep}
                  sx={{
                    py: 1.8,
                    bgcolor: '#06b6d4',
                    color: '#fff',
                    borderRadius: '12px',
                    fontWeight: 800,
                    fontSize: '16px',
                    textTransform: 'none',
                    boxShadow: '0 0 30px rgba(6,182,212,0.4)',
                    '&:hover': { bgcolor: '#0891b2' },
                  }}
                >
                  <LockIcon sx={{ fontSize: 18, mr: 1 }} />
                  Pay ₹4,999 & Start 48-Hr Delivery Now
                </Button>

                {/* Secondary Escape Hatch (The Smart Entrepreneur Move) */}
                <Button
                  onClick={handleTalkToLead}
                  sx={{
                    py: 1.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: '#cbd5e1',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '13px',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.08)',
                      borderColor: '#10b981',
                      color: '#fff',
                    },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#10b981', mr: 1 }} />
                  Have a question first? Save my configuration & talk to Launch Lead
                </Button>
              </Box>
            </Box>
          )}

          {/* ========================================================================= */}
          {/* STEP 4: Post-Payment Asset Drop (Or "Do It Later" Magic Link) */}
          {/* ========================================================================= */}
          {currentStep === 4 && (
            <Box sx={{ maxWidth: '640px', mx: 'auto', pt: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'rgba(16,185,129,0.2)',
                    border: '2px solid #10b981',
                    color: '#34d399',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>
                  🎉 Welcome Aboard, {startupName}!
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '14px' }}>
                  Your 48-hour build clock is officially ticking. Order ID: <code style={{ color: '#22d3ee' }}>VAI-{leadId.slice(-6).toUpperCase()}</code>
                </Typography>
              </Box>

              <Box sx={{ p: 3.5, borderRadius: '16px', bgcolor: wizCardBg, border: `1px solid ${wizBorder}`, mb: 3 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff', mb: 2 }}>
                  Drop Assets & Custom Email Handles (Optional Now)
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#cbd5e1', mb: 0.5 }}>
                      Choose your 2 custom domain business email addresses:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField
                        size="small"
                        value={emailAlias1}
                        onChange={(e) => setEmailAlias1(e.target.value)}
                        placeholder="founder"
                        helperText={`@${cleanDomainSlug}.com`}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)', color: '#fff' },
                          '& .MuiFormHelperText-root': { color: '#22d3ee', fontFamily: 'monospace' },
                        }}
                      />
                      <TextField
                        size="small"
                        value={emailAlias2}
                        onChange={(e) => setEmailAlias2(e.target.value)}
                        placeholder="support"
                        helperText={`@${cleanDomainSlug}.com`}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)', color: '#fff' },
                          '& .MuiFormHelperText-root': { color: '#22d3ee', fontFamily: 'monospace' },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#cbd5e1', mb: 0.5 }}>
                      Launchpad Notes & Configuration for Verina AI:
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={assetNotes}
                      onChange={(e) => setAssetNotes(e.target.value)}
                      placeholder="Paste key facts, pricing, or product specs..."
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' },
                      }}
                    />
                  </Box>

                  <Button
                    onClick={() => setAssetsSaved(true)}
                    sx={{
                      py: 1.4,
                      bgcolor: '#06b6d4',
                      color: '#fff',
                      borderRadius: '10px',
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    {assetsSaved ? '✓ Assets Saved' : 'Save Details to Project Workspace'}
                  </Button>
                </Box>
              </Box>

              {/* Or Do It Later Magic Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  onClick={() => {
                    const text = encodeURIComponent(
                      `Hi! Here is my workspace upload link for ${startupName}: https://verveai.co/project/${leadId}`
                    );
                    window.open(`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${text}`, '_blank');
                  }}
                  sx={{
                    color: '#34d399',
                    fontSize: '13px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 16, mr: 0.8 }} />
                  Send Magic Link to my WhatsApp to upload files later
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* ========================================================================= */}
      {/* 2. THE EXIT-INTENT & DROP-OFF SAFETY NET MICRO-MODAL */}
      {/* ========================================================================= */}
      <Dialog
        open={showExitIntentModal}
        onClose={() => setShowExitIntentModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#131418',
            color: '#fff',
            borderRadius: '20px',
            border: '1px solid rgba(245,158,11,0.4)',
            boxShadow: '0 0 50px rgba(245,158,11,0.2)',
            p: 3,
          },
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              bgcolor: 'rgba(245,158,11,0.15)',
              border: '1px solid rgba(245,158,11,0.4)',
              color: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ShieldOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>

          <Typography sx={{ fontSize: '18px', fontWeight: 800, color: wizText, mb: 1 }}>
            Wait! Don't lose your LAUNCH50 (₹4,999) reservation for{' '}
            <span style={{ color: '#f59e0b' }}>{startupName || 'your startup'}</span>.
          </Typography>

          <Typography sx={{ fontSize: '13px', color: '#94a3b8', mb: 3, lineHeight: 1.5 }}>
            Not ready to pay right this second? No problem. We've saved your blueprint and 50% discount code.
          </Typography>

          {exitCallbackRequested ? (
            <Alert severity="success" sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#34d399', mb: 2 }}>
              ✓ Request sent to our engineering lead. We'll WhatsApp/call within 15 mins!
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Button 1: Green WhatsApp */}
              <Button
                onClick={handleSendBlueprintWhatsApp}
                sx={{
                  py: 1.5,
                  bgcolor: '#10b981',
                  color: '#fff',
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '13px',
                  textTransform: 'none',
                  boxShadow: '0 0 20px rgba(16,185,129,0.3)',
                  '&:hover': { bgcolor: '#059669' },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 18, mr: 1 }} />
                Send Blueprint & ₹4,999 Lock-in Link to my WhatsApp
              </Button>

              {/* Button 2: Subtle Callback */}
              <Button
                onClick={handleRequestCallback}
                sx={{
                  py: 1.3,
                  color: '#cbd5e1',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' },
                }}
              >
                <PhoneInTalkIcon sx={{ fontSize: 16, mr: 1, color: '#f59e0b' }} />
                Have our Launch Architect call me in 15 mins
              </Button>
            </Box>
          )}

          <Button
            onClick={() => {
              setShowExitIntentModal(false);
              onClose();
            }}
            sx={{ mt: 2, fontSize: '11px', color: '#64748b', textTransform: 'none' }}
          >
            I understand, exit anyway
          </Button>
        </Box>
      </Dialog>

      {/* ========================================================================= */}
      {/* 3. SIMULATED / SECURE RAZORPAY / CARD CHECKOUT MODAL */}
      {/* ========================================================================= */}
      <Dialog
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0e1015',
            color: '#fff',
            borderRadius: '20px',
            border: '1px solid rgba(6,182,212,0.3)',
            p: 3,
          },
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              Verve AI Labs • Secure Checkout
            </Typography>
            <IconButton size="small" onClick={() => setShowPaymentModal(false)} sx={{ color: '#94a3b8' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)', mb: 2.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>Total Payable</Typography>
            <Typography sx={{ fontSize: '28px', fontWeight: 900, color: '#fff' }}>₹4,999</Typography>
            <Typography sx={{ fontSize: '11px', color: '#22d3ee' }}>Turnkey Startup Launchpad ({startupName})</Typography>
          </Box>

          <Typography sx={{ fontSize: '12px', color: '#cbd5e1', mb: 2 }}>
            Instant Payment via UPI / Cards / Net Banking
          </Typography>

          <Button
            fullWidth
            onClick={handleConfirmPayment}
            disabled={isProcessingPayment}
            sx={{
              py: 1.6,
              bgcolor: '#10b981',
              color: '#fff',
              borderRadius: '12px',
              fontWeight: 800,
              fontSize: '15px',
              textTransform: 'none',
              mb: 1.5,
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {isProcessingPayment ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: '#fff' }} />
                <span>Authorizing & Creating Project...</span>
              </Box>
            ) : (
              'Confirm ₹4,999 & Launch 48-Hr Delivery'
            )}
          </Button>

          <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
            🔒 256-Bit SSL Encrypted. Covered by 100% On-Time Delivery Guarantee.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}
